// src/components/renderers/MatchingRenderer.js
import React from 'react';

const MatchingRenderer = ({ 
  question, 
  userAnswer = {}, 
  onAnswerChange, 
  showFeedback,
  showCorrectAnswers 
}) => {
  const content = question.content || {};
  const pairs = content.pairs || [];
  const instructions = content.instructions || '';

  // Get unique left and right items (shuffled right items)
  const leftItems = pairs.map(p => p.left);
  const rightItems = React.useMemo(() => {
    const items = pairs.map(p => p.right);
    return items.sort(() => Math.random() - 0.5);
  }, [question.id]);

  const handleMatch = (leftItem, rightItem) => {
    if (!onAnswerChange || showFeedback) return;
    
    const newAnswer = { ...userAnswer };
    
    // If this right item is already matched, unassign it first
    Object.keys(newAnswer).forEach(key => {
      if (newAnswer[key] === rightItem && key !== leftItem) {
        delete newAnswer[key];
      }
    });
    
    // Toggle: if already matched to this left item, unmatch it
    if (newAnswer[leftItem] === rightItem) {
      delete newAnswer[leftItem];
    } else {
      newAnswer[leftItem] = rightItem;
    }
    
    onAnswerChange(newAnswer);
  };

  const getCorrectMatch = (leftItem) => {
    return pairs.find(p => p.left === leftItem)?.right || '';
  };

  const isCorrectMatch = (leftItem) => {
    return userAnswer[leftItem] === getCorrectMatch(leftItem);
  };

  const isRightItemUsed = (rightItem) => {
    return Object.values(userAnswer).includes(rightItem);
  };

  return (
    <div className="question-renderer matching-renderer">
      <div 
        className="question-instructions" 
        dangerouslySetInnerHTML={{ __html: instructions }} 
      />

      <div className="matching-container">
        <div className="matching-left-column">
          <h4>Match these:</h4>
          {leftItems.map((leftItem, index) => {
            const selectedRight = userAnswer[leftItem];
            const isCorrect = (showFeedback || showCorrectAnswers) && isCorrectMatch(leftItem);
            
            return (
              <div 
                key={index} 
                className={`matching-left-item ${
                  (showFeedback || showCorrectAnswers)
                    ? (isCorrect ? 'correct-match' : 'incorrect-match')
                    : ''
                }`}
              >
                <div className="item-text">{leftItem}</div>
                <div className="selected-match">
                  {selectedRight && (
                    <>
                      <span className="arrow">→</span>
                      <span className="matched-text">{selectedRight}</span>
                    </>
                  )}
                </div>
                {(showFeedback || showCorrectAnswers) && !isCorrect && (
                  <div className="correct-match-display">
                    Correct: {getCorrectMatch(leftItem)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="matching-right-column">
          <h4>With these:</h4>
          {rightItems.map((rightItem, index) => (
            <div 
              key={index} 
              className={`matching-right-item ${
                isRightItemUsed(rightItem) ? 'used' : ''
              } ${showFeedback ? 'disabled' : ''}`}
              onClick={() => {
                // Find the selected left item (most recent or first unmatched)
                const selectedLeft = leftItems.find(left => !userAnswer[left]) || leftItems[0];
                handleMatch(selectedLeft, rightItem);
              }}
            >
              <div className="item-text">{rightItem}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="matching-instructions-help">
        <p>💡 Click on items from the right column to match them with items on the left</p>
      </div>

      {showFeedback && content.generalFeedback && (
        <div className="feedback-section">
          <div className="general-feedback">
            <strong>Feedback:</strong> {content.generalFeedback}
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchingRenderer;